import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import { Modal } from '../../components/Common';
import authService from '../../services/authService';
import { updateUser } from '../../store/slices/authSlice';

const getErrorMessage = (err, fallback) =>
  err.response?.data?.message || err.response?.data?.error || fallback;

const ChangeMobileModal = ({ isOpen, onClose }) => {
  const [step, setStep] = useState('details'); // 'details' | 'otp'
  const [currentPassword, setCurrentPassword] = useState('');
  const [newMobile, setNewMobile] = useState('');
  const [otp, setOtp] = useState('');
  const [otpMessage, setOtpMessage] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const dispatch = useDispatch();
  const { user } = useSelector((state) => state.auth);

  const handleClose = () => {
    setStep('details');
    setCurrentPassword('');
    setNewMobile('');
    setOtp('');
    setOtpMessage('');
    onClose();
  };

  const handleSendOtp = async (e) => {
    if (e) e.preventDefault();
    if (!currentPassword) {
      toast.error('Please enter your current password');
      return;
    }
    if (newMobile.length !== 10) {
      toast.error('Please enter a valid 10-digit mobile number');
      return;
    }
    setIsSubmitting(true);
    try {
      const response = await authService.sendChangeMobileOtp(currentPassword, newMobile);
      setOtpMessage(response.message);
      toast.success(response.message);
      setStep('otp');
    } catch (err) {
      toast.error(getErrorMessage(err, 'Failed to send OTP'));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleVerify = async (e) => {
    e.preventDefault();
    if (otp.length !== 6) {
      toast.error('Please enter the 6-digit OTP');
      return;
    }
    setIsSubmitting(true);
    try {
      const response = await authService.verifyChangeMobile(otp);
      dispatch(updateUser({ mobile: response.user.mobile }));
      toast.success(response.message);
      handleClose();
    } catch (err) {
      toast.error(getErrorMessage(err, 'Failed to verify OTP'));
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={handleClose} title="Change Mobile Number" size="sm">
      {step === 'details' ? (
        <form onSubmit={handleSendOtp}>
          <p style={{ fontSize: '13px', color: '#6b7280', marginBottom: '16px' }}>
            Current mobile: <strong>{user?.mobile || 'Not set'}</strong>. Password reset OTPs are
            sent to this number.
          </p>
          <div className="form-group">
            <label className="form-label">New Mobile Number</label>
            <input
              type="tel"
              inputMode="numeric"
              className="form-control"
              placeholder="10-digit mobile number"
              value={newMobile}
              onChange={(e) => setNewMobile(e.target.value.replace(/\D/g, ''))}
              maxLength={10}
            />
          </div>
          <div className="form-group">
            <label className="form-label">Current Password</label>
            <input
              type="password"
              className="form-control"
              value={currentPassword}
              onChange={(e) => setCurrentPassword(e.target.value)}
              autoComplete="current-password"
            />
          </div>
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '12px' }}>
            <button type="button" className="btn btn-secondary" onClick={handleClose}>
              Cancel
            </button>
            <button type="submit" className="btn btn-primary" disabled={isSubmitting}>
              {isSubmitting ? 'Sending...' : 'Send OTP'}
            </button>
          </div>
        </form>
      ) : (
        <form onSubmit={handleVerify}>
          <div className="form-group">
            <label className="form-label">Enter OTP</label>
            <input
              type="text"
              inputMode="numeric"
              className="form-control"
              placeholder="Enter 6-digit OTP"
              value={otp}
              onChange={(e) => setOtp(e.target.value.replace(/\D/g, ''))}
              maxLength={6}
              autoComplete="one-time-code"
            />
            <p style={{ fontSize: '12px', color: '#6b7280', marginTop: '8px' }}>
              {otpMessage}{' '}
              <button
                type="button"
                onClick={() => handleSendOtp()}
                disabled={isSubmitting}
                style={{
                  background: 'none',
                  border: 'none',
                  padding: 0,
                  color: 'var(--primary)',
                  cursor: 'pointer',
                  fontSize: '12px',
                }}
              >
                Resend OTP
              </button>
            </p>
          </div>
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '12px' }}>
            <button type="button" className="btn btn-secondary" onClick={() => setStep('details')}>
              Back
            </button>
            <button type="submit" className="btn btn-primary" disabled={isSubmitting}>
              {isSubmitting ? 'Verifying...' : 'Verify & Update'}
            </button>
          </div>
        </form>
      )}
    </Modal>
  );
};

export default ChangeMobileModal;
